import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  useLocation,
  useMatch,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { REPORT_QUERY_PARAMS } from "@sscs/report/constants";
import {
  FILESTREAM_PURL,
  FILESTREAM_ROUTE,
  FULL_REPORT_PATH,
  PROJECTS_ROUTE,
} from "Constants";
import {
  removeAllReportsThunk,
  removeReportsThunk,
  selectReports,
} from "Store/reports.slice";
import { ReportNavigationState } from "Types";
import { generateReportKey, generateReportReturnToKey } from "Utils";
import { useAppDispatch } from "./index";

const useReports = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { reports, deletedReports } = useSelector(selectReports);
  const { state: routerState } = useLocation();
  const { groupId, projectName, packageName, version } = useParams();
  const [searchParams] = useSearchParams();

  const isReportPath = Boolean(
    useMatch({
      path: `${FULL_REPORT_PATH}/*`,
    }),
  );

  const build = searchParams.get(REPORT_QUERY_PARAMS.build);
  const diffWith = searchParams.get(REPORT_QUERY_PARAMS.diff_with);
  const openedReportKey = isReportPath
    ? generateReportKey({
        groupId: groupId ?? "",
        projectName: projectName ?? "",
        packageName: packageName ?? "",
        version: version ?? "",
        build: build,
        diffWith: diffWith ?? undefined,
      })
    : undefined;

  useEffect(() => {
    if (!openedReportKey) {
      return;
    }

    if (routerState) {
      sessionStorage.setItem(
        generateReportReturnToKey(openedReportKey),
        JSON.stringify(routerState),
      );
    }
  }, [openedReportKey, routerState]);

  const closeReport = useCallback(
    (id: string) => {
      const returnToRawData = sessionStorage.getItem(
        generateReportReturnToKey(id),
      );

      dispatch(removeReportsThunk([id]));

      if (openedReportKey === id) {
        if (returnToRawData) {
          const returnTo = JSON.parse(returnToRawData) as ReportNavigationState;

          navigate(returnTo.from);
        } else {
          if (pathname.includes(FILESTREAM_PURL.projectName))
            navigate(`/${FILESTREAM_ROUTE}/group/${groupId}`);
          else navigate(`/${PROJECTS_ROUTE}/group/${groupId}`);
        }
      }
    },
    [dispatch, groupId, navigate, openedReportKey, pathname],
  );

  const closeAllReports = useCallback(
    ({
      groupId,
      projectName,
      packageName,
      version,
    }: {
      groupId: string | undefined;
      projectName: string;
      packageName?: string;
      version?: string;
    }) => {
      if (groupId) {
        dispatch(
          removeAllReportsThunk({ groupId, projectName, packageName, version }),
        );
      }
    },
    [dispatch],
  );

  return {
    reports,
    deletedReports,
    openedReportKey,
    isReportPath,
    closeReport,
    closeAllReports,
  };
};

export default useReports;
